'use client';

/**
 * Tools feed renderer. Threads tool.call + tool.result events by
 * turnId / response_id into call→result cards, then defers to JsonLogRenderer
 * for the full raw stream.
 */
import React, { useMemo, useState } from 'react';
import { JsonLogRenderer, type FeedRendererProps } from './JsonLogRenderer';
import type { AgentDebugEvent } from '@/lib/debug/types';

function payloadObj(ev: AgentDebugEvent): Record<string, unknown> {
  return ev.payload && typeof ev.payload === 'object' ? (ev.payload as Record<string, unknown>) : {};
}
function corrKey(ev: AgentDebugEvent): string {
  const p = payloadObj(ev);
  return String(p.response_id ?? ev.turnId ?? p.request_source ?? ev.id);
}

interface ToolPair {
  key: string;
  call?: AgentDebugEvent;
  result?: AgentDebugEvent;
}

export function ToolsRenderer(props: FeedRendererProps) {
  const { events } = props;
  const [showRaw, setShowRaw] = useState(true);

  const pairs = useMemo<ToolPair[]>(() => {
    const m = new Map<string, ToolPair>();
    for (const ev of events) {
      if (ev.kind !== 'tool.call' && ev.kind !== 'tool.result') continue;
      const k = corrKey(ev);
      const cur = m.get(k) ?? { key: k };
      if (ev.kind === 'tool.call') cur.call = ev;
      else cur.result = ev;
      m.set(k, cur);
    }
    // Newest first by max ts in the pair.
    return Array.from(m.values()).sort((a, b) => {
      const at = Math.max(a.call?.ts ?? 0, a.result?.ts ?? 0);
      const bt = Math.max(b.call?.ts ?? 0, b.result?.ts ?? 0);
      return bt - at;
    });
  }, [events]);

  const json = (v: unknown) => {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-white/[0.12] bg-[#0d0d0d] px-2 py-1.5 text-[11px]">
        <div className="flex items-center">
          <span className="text-slate-400">{pairs.length} tool interaction{pairs.length === 1 ? '' : 's'}</span>
          <button
            onClick={() => setShowRaw((v) => !v)}
            className="ml-auto rounded border border-white/[0.12] px-1.5 py-0.5 text-slate-300 hover:bg-white/[0.06]"
          >
            {showRaw ? 'hide raw stream' : 'show raw stream'}
          </button>
        </div>
      </div>
      <div className={`overflow-auto ${showRaw ? 'max-h-[45%]' : 'flex-1'} bg-[#0a0a0a]`}>
        {pairs.length === 0 ? (
          <div className="p-3 text-[11px] text-slate-600">no tool calls</div>
        ) : (
          pairs.map((pr) => {
            const cp = pr.call ? payloadObj(pr.call) : {};
            const rp = pr.result ? payloadObj(pr.result) : {};
            const err = rp.error;
            return (
              <div key={pr.key} className="border-b border-white/[0.06] px-2 py-1.5 font-mono text-[11px]">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-indigo-900/50 px-1 text-indigo-300">{String(cp.tool_name ?? rp.tool_name ?? 'tool')}</span>
                  {pr.call?.turnId && <span className="text-slate-600">{pr.call.turnId}</span>}
                  {typeof rp.elapsed_ms === 'number' && <span className="text-slate-500">{rp.elapsed_ms}ms</span>}
                  {err ? <span className="rounded bg-red-900/50 px-1 text-red-300">error</span> : null}
                  {!pr.result && <span className="text-amber-400">(no result event)</span>}
                </div>
                {pr.call && (
                  <details className="mt-0.5">
                    <summary className="cursor-pointer text-slate-400">args</summary>
                    <pre className="mt-0.5 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-black/30 p-1 text-emerald-300">{json(cp.args)}</pre>
                  </details>
                )}
                {pr.result && (
                  <details className="mt-0.5">
                    <summary className="cursor-pointer text-slate-400">{err ? 'error' : 'result'}</summary>
                    <pre className="mt-0.5 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-black/30 p-1 text-emerald-300">{json(err ?? rp.result)}</pre>
                  </details>
                )}
              </div>
            );
          })
        )}
      </div>
      {showRaw && (
        <div className="min-h-0 flex-1 border-t border-white/[0.12]">
          <JsonLogRenderer {...props} />
        </div>
      )}
    </div>
  );
}

export default ToolsRenderer;
